/**
 * `useResyncOnReconnect(status, onReconnect)` — run a recovery callback
 * when the shared WebSocket comes back after having been down.
 *
 * WHY THIS EXISTS: realtime frames are fire-and-forget. Per ADR-033 a
 * hidden tab drops its socket after a 60s grace (and any tab after 10
 * minutes idle), and frames sent while it is down are NOT queued or
 * replayed — they are simply lost. So every cache fed by WS frames is
 * silently stale after a reconnect until something refetches it.
 *
 * The trap this addresses is that the UI looks healthy meanwhile: the
 * sidebar reads "Live" the moment the socket is back, which means
 * "connected from now on" and says nothing about what was missed. A user
 * alt-tabbing away, receiving a DM, and returning saw no unread badge at
 * all until a manual refresh.
 *
 * ONLY fires on a TRUE reconnect — `open` AFTER a prior `open`. The
 * initial connect is skipped, because at that point every query is doing
 * its own first load and invalidating would just double-fetch.
 *
 * THIS IS NOT A KEEP-ALIVE, and must not become one. It runs once, at
 * the instant the socket returns — i.e. when the user is already active
 * — and touches nothing about the socket lifecycle. Hidden tabs still
 * disconnect and Aurora can still auto-pause, which is the entire point
 * of ADR-033. Two tempting "improvements" both erode that and are
 * deliberately rejected: flipping `refetchOnWindowFocus` to `true` (it
 * fires on every focus whether or not anything was missed) and any
 * polling / `refetchInterval`.
 *
 * Known gap: a flaky connection reconnecting repeatedly will re-run the
 * callback each time. `useGlobalRealtime`'s hand-rolled copy of this
 * pattern has the same exposure and does not guard it; matched here for
 * consistency rather than debounced. If that ever bites, debounce HERE
 * so every caller inherits the fix.
 */

import { useEffect, useRef } from 'react'

import type { WebSocketStatus } from './use-websocket'

export function useResyncOnReconnect(status: WebSocketStatus, onReconnect: () => void): void {
  // The callback is captured in a ref so an inline arrow at the call
  // site doesn't re-run the effect on every render — the effect must be
  // driven by `status` transitions alone.
  const onReconnectRef = useRef(onReconnect)
  useEffect(() => {
    onReconnectRef.current = onReconnect
  }, [onReconnect])

  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (status !== 'open') return
    if (!wasOpenRef.current) {
      wasOpenRef.current = true
      return
    }
    onReconnectRef.current()
  }, [status])
}
